import { type FormSummaryJSONObject, IkeToken } from '@ikegps/core-api';
import { action, computed, observable, runInAction, makeObservable } from 'mobx';
import { FabButton } from '../../components/button/fab.button';
import { ImageButton } from '../../components/button/image.button';
import { ImportPoleforemanButton } from '../../components/button/import-poleforeman.button';
import { ImportButton } from '../../components/button/import.button';
import { MaterialIcon } from '../../components/icon/icons';
import { Spinner } from '../../components/spinner/spinner';
import { MenuComponent } from '../../components/menu/menu';
import { MenuItemComponent } from '../../components/menu/menu.item';
import { IMithrilStateComponent } from '../../components/mithril.component';
import { Table } from '../../components/table/table';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n/index';
import { m, type MithrilVNode } from '../../mithril';
import { IkePageName } from '../../page/page';
import { Services } from '../../services';
import { AppStore } from '../../store/index';
import { FormCopyModal } from '../form.copy.modal/form.copy.modal';
import { FormShareModal } from '../form.share.modal/form.share.modal';
import { FormArchiveConfirmModal } from './form.archive.confirm.modal';
import { FormDeleteConfirmModal } from './form.delete.confirm.modal';
import { FormRestoreConfirmModal } from './form.restore.confirm.modal';
import { FormImportCtrl } from './form.import.ctrl';
import { FormImportModal } from './form.import.modal';
import { FormImportPoleforemanModal } from './form.import.poleforeman.modal';
import { CSS, Style } from './form.list.style';

addStyles(Style);

/** Display a list of forms */
export class FormList extends IMithrilStateComponent {
	menu: MenuComponent;
	archivedMenu: MenuComponent;
	@observable selectedForm: FormSummaryJSONObject | null = null;
	@observable selectedArchivedForm: FormSummaryJSONObject | null = null;
	@observable archivedExpanded = false;
	@observable validIPFLicense: boolean | null = null;
	@observable formRefreshLoading: string | null = null;
	@observable databases: string[] = observable.array([], { deep: false }) as any;

	constructor() {
		super();

		makeObservable(this);

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.checkForValidIPFLicense();

		this.archivedMenu = new MenuComponent({
			items: () => [
				{
					view: MenuItemComponent.view(Translate(T.FORM_RESTORE)),
					action: () => this.confirmRestoreForm(),
					visible: Services.Token.has(IkeToken.FEATURE_FORM_DELETE),
				},
			],
		});

		this.menu = new MenuComponent({
			items: () => {
				if (!Services.User.current?.objectId) {
					return [];
				}

				const currentUser = Services.UserProfile.get(Services.User.current?.objectId);
				if (!currentUser) {
					return [];
				}

				return [
					{
						view: MenuItemComponent.view(Translate(T.FORM_DUPLICATE)),
						action: () => this.copySelectedForm(),
						visible:
							Services.UserProfile.isDepartmentAdmin(currentUser) ||
							Services.UserProfile.isDepartmentDataAdmin(currentUser),
					},
					{
						view: MenuItemComponent.view(Translate(T.FORM_COPY_TO_DEPARTMENT)),
						action: () => this.confirmCopyForm(),
						visible:
							Services.UserProfile.isDepartmentAdmin(currentUser) ||
							Services.UserProfile.isDepartmentDataAdmin(currentUser),
					},
					{
						view: MenuItemComponent.view(Translate(T.FORM_SHARE)),
						action: () => this.confirmShareForm(),
						visible:
							Services.Token.has(IkeToken.FEATURE_FORM_SHARE) &&
							Services.Department.moveableDepartments().length > 0 &&
							(Services.UserProfile.isDepartmentAdmin(currentUser) ||
								Services.UserProfile.isDepartmentDataAdmin(currentUser)),
					},
					{
						view: MenuItemComponent.view(Translate(T.FORM_REFRESH)),
						action: () => this.confirmRegenerateIKEPoleForemanForm(),
						visible: this.refreshFormOptionVisible,
					},
					{
						view: MenuItemComponent.view(Translate(T.FORM_ARCHIVE)),
						action: () => this.confirmArchiveForm(),
						visible: this.canManageArchive,
					},
					{
						view: MenuItemComponent.view(Translate(T.FORM_DELETE)),
						action: () => this.confirmDeleteForm(),
						visible: this.canDeleteForm,
					},
				];
			},
		});
	}

	protected async copySelectedForm() {
		if (!this.selectedForm) {
			return;
		}
		await Services.Form.copy(this.selectedForm.objectId);
		runInAction(() => {
			AppStore.FormSummary.refresh();
		});
	}

	@computed
	get selectedFormUuid() {
		return this.selectedForm?.uuid ?? null;
	}

	get refreshFormOptionVisible(): boolean {
		const isIkeAnalyst = Services.User.isIkeAnalyst(Services.User.current);

		if (this.selectedFormUuid == null) {
			return false;
		}
		const form = AppStore.FormSummary.all.find((summary) => summary.uuid === this.selectedFormUuid);
		const isIPFForm = form?.metadata?.ikePoleForeman || false;
		if (!isIPFForm) {
			return false;
		}
		const currentOrgAccessId = Services.Department.currentOrg?.accessOrgId;
		if (!currentOrgAccessId) {
			return false;
		}
		const formIsFromSameAccessOrg = form?.metadata?.ikePoleForeman?.accessOrgId === currentOrgAccessId;
		if (!formIsFromSameAccessOrg) {
			return false;
		}
		const hasValidPermissions =
			this.validIPFLicense === true && (isIkeAnalyst || Services.Token.has(IkeToken.FEATURE_IPF_DB_IMPORT));
		return hasValidPermissions;
	}

	get nodes(): MithrilVNode {
		const isIkeAnalyst = Services.User.isIkeAnalyst(Services.User.current);
		const hasImportToken = Services.Token.has(IkeToken.FEATURE_IMPORT_FORM);

		const hasIPFImportToken = Services.Token.has(IkeToken.FEATURE_IPF_DB_IMPORT);

		return m('div', { className: CSS.container }, [
			this.viewForms(),
			this.viewArchivedForms(),
			FabButton.view({
				title: T.FORM_CREATE,
				onclick: () => Services.Route.go(IkePageName.formNew),
			}),
			...(isIkeAnalyst || hasImportToken
				? [
						ImportButton.view({
							title: T.FORM_IMPORT,
							onclick: () => {
								const formImportModalCtrl = new FormImportCtrl();
								const formImportModal = new FormImportModal(formImportModalCtrl);
								formImportModal.open();
							},
						}),
					]
				: []),
			...(isIkeAnalyst || hasIPFImportToken
				? [
						ImportPoleforemanButton.view({
							title: T.FORM_NEW_POLEFOREMAN_FORM,
							onclick: () => {
								const formImportPoleForemanModal = new FormImportPoleforemanModal({
									title: T.FORM_IMPORT_POLEFOREMAN,
									confirm: T.FORM_IMPORT_POLEFOREMAN_OK,
									success: { key: T.FORM_IMPORT_SUCCEED },
								});
								formImportPoleForemanModal.open();
							},
						}),
					]
				: []),
		]);
	}

	private viewForms(): MithrilVNode {
		if (AppStore.FormSummary.isLoading) {
			return m('div', { className: CSS.loading }, Spinner.view());
		}

		const headers: string[] = [
			'#',
			`${Translate(T.FORM_NAME)} (${Translate(T.FORM_VERSION)})`,
			Translate(T.FORM_DESCRIPTION),
			Translate(T.FORM_UPDATED_AT),
			Translate(T.FORM_CREATED_AT),
			'',
		];
		const rows: MithrilVNode[][] = [];

		const forms = AppStore.FormSummary.all;
		for (let i = 0; i < forms.length; i++) {
			rows.push(this.viewForm(forms[i]!, i));
		}

		return Table.view(Translate(T.FORM_TITLE), headers, rows);
	}

	private viewForm(form: FormSummaryJSONObject, index: number): MithrilVNode[] {
		let displayName = form.displayname;
		if (form.displayversion && form.displayversion > 0) {
			displayName = `${displayName} (V${form.displayversion})`;
		}
		return [
			m('span', { className: CSS.tableCell }, String(index + 1)),
			m(
				'a',
				{
					className: `mdl-button mdl-ripple-effect mdl-button--accent ${CSS.tableCell} ${CSS.tableCellButton}`,
					title: Translate(T.FORM_EDIT),
					'data-testid': 'form-name',
					href: Services.Route.getRouteURL(IkePageName.formEdit, {
						version: form.revision,
					}),
				},
				displayName
			),
			m('div', { className: `${CSS.tableCell} ${CSS.tableCellFaint} ` }, form.description),
			m(
				'div',
				{ className: `${CSS.tableCell} ${CSS.tableCellFaint} ` },
				Services.DateTime.formatDateTime(form.updatedAt)
			),
			m(
				'div',
				{ className: `${CSS.tableCell} ${CSS.tableCellFaint} ` },
				Services.DateTime.formatDateTime(form.createdAt)
			),
			m('div', { className: CSS.tableCellMenu }, this.viewFormActions(form)),
		];
	}

	private viewFormActions(form: FormSummaryJSONObject): MithrilVNode | MithrilVNode[] {
		if (!this.menu.hasUsableItems) {
			return null;
		}

		return [
			ImageButton.view(MaterialIcon.MORE_VERT, {
				onclick: () => {
					this.setSelectedForm(form);
					this.menu.show();
				},
				testId: 'form-menu',
			}),
			this.selectedFormUuid === form.uuid ? this.menu.view() : null,
		];
	}

	@action
	private setSelectedForm(form: FormSummaryJSONObject) {
		this.selectedForm = form;
	}

	private async confirmArchiveForm() {
		if (this.selectedFormUuid == null) {
			return;
		}

		const form = AppStore.FormSummary.all.find((summary) => summary.uuid === this.selectedFormUuid);

		if (!form) {
			return;
		}

		const confirmModal = new FormArchiveConfirmModal(
			{
				title: T.FORM_ARCHIVE_TITLE,
				confirm: T.FORM_ARCHIVE,
				success: {
					key: T.FORM_ARCHIVE_SUCCESS,
					attrs: {
						name: form.displayname,
					},
				},
			},
			form
		);

		confirmModal.open();
	}

	private async confirmDeleteForm() {
		if (this.selectedFormUuid == null) {
			return;
		}

		const form = AppStore.FormSummary.all.find((summary) => summary.uuid === this.selectedFormUuid);

		if (!form) {
			return;
		}

		const confirmModal = new FormDeleteConfirmModal(
			{
				title: T.FORM_DELETE_TITLE,
				confirm: T.FORM_DELETE,
				success: {
					key: T.FORM_DELETE_SUCCESS,
					attrs: {
						name: form.displayname,
					},
				},
			},
			form
		);

		confirmModal.open();
	}

	private async confirmShareForm() {
		if (this.selectedFormUuid == null) {
			return;
		}

		const form = AppStore.FormSummary.all.find((summary) => summary.uuid === this.selectedFormUuid);

		if (!form) {
			return;
		}

		const confirmModal = new FormShareModal(
			{
				title: T.FORM_SHARE_TITLE,
				confirm: T.FORM_SHARE,
				success: {
					key: T.FORM_SHARE_SUCCESS,
					attrs: {
						name: form.displayname,
					},
				},
			},
			form.revision
		);

		confirmModal.open();
	}

	private async confirmCopyForm() {
		if (!this.selectedForm) {
			return;
		}

		const form = AppStore.FormSummary.all.find((summary) => summary.objectId === this.selectedForm?.objectId);

		if (!form) {
			return;
		}

		const confirmModal = new FormCopyModal(
			{
				title: T.FORM_COPY_TO_DEPARTMENT_TITLE,
				confirm: T.FORM_COPY_TO_DEPARTMENT,
				success: {
					key: T.FORM_COPY_TO_DEPARTMENT_SUCCESS,
					attrs: {
						name: form.displayname,
					},
				},
			},
			form
		);

		confirmModal.open();
	}

	private async confirmRegenerateIKEPoleForemanForm() {
		if (this.selectedFormUuid == null) {
			return;
		}

		const form = AppStore.FormSummary.all.find((summary) => summary.uuid === this.selectedFormUuid);

		if (!form) {
			return;
		}

		const refreshFormModel = new FormImportPoleforemanModal(
			{
				title: T.FORM_REFRESH_MODAL_TITLE,
				// confirm and success will not be used
				confirm: T.FORM_IMPORT_POLEFOREMAN_OK,
				success: {
					key: T.FORM_REFRESH_SUCCESS,
					attrs: {
						name: form.displayname,
					},
				},
				cancel: T.CLOSE,
			},
			form.revision
		);

		refreshFormModel.open();
	}

	async checkForValidIPFLicense() {
		const rest = Services.Department.rest;
		const ipfLicenseCheckResult = await rest.Form.Pla.getRefreshPermission({});
		this.validIPFLicense = ipfLicenseCheckResult.success;
	}

	private get canDeleteForm(): boolean {
		if (!Services.Token.has(IkeToken.FEATURE_FORM_DELETE) || Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE)) {
			return false;
		}
		if (!Services.User.current?.objectId) {
			return false;
		}
		const currentUser = Services.UserProfile.get(Services.User.current.objectId);
		if (!currentUser) {
			return false;
		}
		return (
			Services.UserProfile.isDepartmentAdmin(currentUser) || Services.UserProfile.isDepartmentDataAdmin(currentUser)
		);
	}

	private get canManageArchive(): boolean {
		if (!Services.Token.has(IkeToken.FEATURE_FORM_DELETE) || !Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE)) {
			return false;
		}
		if (!Services.User.current?.objectId) {
			return false;
		}
		const currentUser = Services.UserProfile.get(Services.User.current.objectId);
		if (!currentUser) {
			return false;
		}
		return (
			Services.UserProfile.isDepartmentAdmin(currentUser) || Services.UserProfile.isDepartmentDataAdmin(currentUser)
		);
	}

	private viewArchivedForms(): MithrilVNode {
		if (!this.canManageArchive) {
			return null;
		}

		const archivedForms = AppStore.FormSummary.archived;

		if (archivedForms.length === 0) {
			return null;
		}

		const chevronIcon = this.archivedExpanded ? MaterialIcon.KEY_DOWN : MaterialIcon.KEY_RIGHT;

		return m('div', { className: CSS.archivedSection }, [
			m(
				'div',
				{
					className: CSS.archivedHeader,
					onclick: () => this.toggleArchivedExpanded(),
				},
				[
					m('i', { className: `material-icons ${CSS.archivedHeaderIcon}` }, chevronIcon),
					`${Translate(T.FORM_ARCHIVED_TITLE)} (${archivedForms.length})`,
				]
			),
			this.archivedExpanded ? this.viewArchivedFormsTable(archivedForms) : null,
		]);
	}

	@action
	private toggleArchivedExpanded() {
		this.archivedExpanded = !this.archivedExpanded;
	}

	private viewArchivedFormsTable(forms: FormSummaryJSONObject[]): MithrilVNode {
		const headers: string[] = [
			'#',
			`${Translate(T.FORM_NAME)} (${Translate(T.FORM_VERSION)})`,
			Translate(T.FORM_DESCRIPTION),
			Translate(T.FORM_UPDATED_AT),
			'',
		];

		const rows: MithrilVNode[][] = [];
		for (let i = 0; i < forms.length; i++) {
			rows.push(this.viewArchivedForm(forms[i]!, i));
		}

		return Table.view('', headers, rows);
	}

	private viewArchivedForm(form: FormSummaryJSONObject, index: number): MithrilVNode[] {
		let displayName = form.displayname;
		if (form.displayversion && form.displayversion > 0) {
			displayName = `${displayName} (V${form.displayversion})`;
		}
		return [
			m('span', { className: `${CSS.tableCell} ${CSS.archivedRow}` }, String(index + 1)),
			m('span', { className: `${CSS.tableCell} ${CSS.archivedRow}` }, displayName),
			m('div', { className: `${CSS.tableCell} ${CSS.tableCellFaint} ${CSS.archivedRow}` }, form.description),
			m(
				'div',
				{ className: `${CSS.tableCell} ${CSS.tableCellFaint} ${CSS.archivedRow}` },
				Services.DateTime.formatDateTime(form.updatedAt)
			),
			m('div', { className: CSS.tableCellMenu }, this.viewArchivedFormActions(form)),
		];
	}

	private viewArchivedFormActions(form: FormSummaryJSONObject): MithrilVNode | MithrilVNode[] {
		if (!this.archivedMenu.hasUsableItems) {
			return null;
		}

		return [
			ImageButton.view(MaterialIcon.MORE_VERT, {
				onclick: () => {
					this.setSelectedArchivedForm(form);
					this.archivedMenu.show();
				},
			}),
			this.selectedArchivedForm?.uuid === form.uuid ? this.archivedMenu.view() : null,
		];
	}

	@action
	private setSelectedArchivedForm(form: FormSummaryJSONObject) {
		this.selectedArchivedForm = form;
	}

	private async confirmRestoreForm() {
		if (this.selectedArchivedForm == null) {
			return;
		}

		const form = AppStore.FormSummary.archived.find((summary) => summary.uuid === this.selectedArchivedForm?.uuid);

		if (!form) {
			return;
		}

		const confirmModal = new FormRestoreConfirmModal(
			{
				title: T.FORM_RESTORE_TITLE,
				confirm: T.FORM_RESTORE,
				success: {
					key: T.FORM_RESTORE_SUCCESS,
					attrs: {
						name: form.displayname,
					},
				},
			},
			form
		);

		confirmModal.open();
	}
}
